import { useRouter } from "next/router";
import { useCallback, useMemo, useRef } from "react";

import { APICompsSchema } from "@/apis/api-config";
import AppAuthApi from "@/apis/connect/auth-api";
import {
  COLORME_CALLBACK_PATH,
  COLORME_INSTALLED_PATH,
  COLORME_LOGIN_PATH,
  DASHBOARD_PATH,
  LOGIN_PATH,
  PASSWORD_RESET_PATH,
  SIGNUP_PATH,
} from "@/global-state/app-path";
import { ADMIN_LOGIN_PATH } from "@/global-state/external-path";
import { firebaseAuth, firebaseGetAuth } from "@/global-state/firebase-settings";
import {
  authUserAtom,
  idTokenAtom,
  initializingAtom,
  instagramAccountInfoAtom,
  loadingAtom,
  refererAtom,
  signupLoadingAtom,
} from "@/global-state/jotai-atoms";
import { LocalForageKeys } from "@/global-state/local-forage-keys";
import { SignupRequests } from "@/utils/app-types";
import { clearColormePendingLink, readColormePendingLink, saveColormePendingLink } from "@/utils/colorme-pending-link";

import { useSignupReferer } from "../firestore/use-signup-referer";

import { isCorporateDemoSession } from "@/constants/corporate-demo";
import {
  EmailAuthProvider,
  FacebookAuthProvider,
  User,
  UserCredential,
  createUserWithEmailAndPassword,
  deleteUser,
  getAdditionalUserInfo,
  linkWithCredential,
  linkWithPopup,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  unlink,
  updateEmail,
  updatePassword,
} from "firebase/auth";
import { useAtom } from "jotai";
import localforage from "localforage";
import { useSnackbar } from "notistack";

// 新規登録の最中かどうか。AuthProvider の onAuthStateChanged リスナーはこの間 member-me を呼ばない。
// users 行がまだ無い段階で member-me を呼ぶと 401 → resetAuth(signOut) になり、登録が途中で壊れる。
// useAuthApi はコンポーネントごとに別インスタンスになるので、フックの state ではなくモジュール変数で共有する。
let signupInProgress = false;

// Firebase のメール/パスワード紐づけに失敗した時の理由。コードのまま出しても分からないので日本語にする
const LINK_ERROR_MESSAGES: Record<string, string> = {
  "auth/email-already-in-use": "このメールアドレスは既に別のアカウントで使われています",
  "auth/credential-already-in-use": "このメールアドレスは既に別のアカウントで使われています",
  "auth/provider-already-linked": "このFacebookアカウントには既に別のメールアドレスが登録されています",
  "auth/invalid-email": "メールアドレスの形式が正しくありません",
  "auth/weak-password": "パスワードが短すぎます",
  "auth/requires-recent-login": "Facebookの認証が古くなりました",
};

// 新規登録の失敗。トーストだと数秒で消えて理由を読めないので、登録画面に残して表示する
export type SignupFailure = {
  kind: "popup" | "link" | "no_instagram" | "already_registered" | "other";
  title: string;
  detail?: string;
  // ログイン画面への案内を出すか(既に登録済み・別アカウントで使用中のとき)
  suggestLogin?: boolean;
};

// member-me が「ユーザーが見つかりません」で断られる = Firebase にはいるが HINOME の会員登録が終わっていない。
// 通信エラーなど他の失敗では false にする(登録済みの人の Firebase ユーザーを消さないため)
const UNREGISTERED_MEMBER_DETAIL = "ユーザーが見つかりませんでした。新規登録をお願いします。";
const isUnregisteredMember = async (accessToken: string | null): Promise<boolean> => {
  try {
    await new AppAuthApi(accessToken).memberMe();
    return false;
  } catch (e) {
    return e?.response?.status === 401 && e?.response?.data?.detail === UNREGISTERED_MEMBER_DETAIL;
  }
};

// 「t***@gmail.com」の形にする。どのメールで登録済みかの見当をつけてもらうため
const maskEmail = (email?: string | null): string => {
  if (!email || !email.includes("@")) return "";
  const [local, domain] = email.split("@");
  return `${local.slice(0, 1)}***@${domain}`;
};

const POPUP_ERROR_MESSAGES: Record<string, SignupFailure> = {
  "auth/popup-closed-by-user": {
    kind: "popup",
    title: "Facebookの画面が閉じられたため、登録を中断しました",
    detail: "もう一度「Facebookと連携する」を押して、Facebookの画面でログインと許可を最後まで進めてください。",
  },
  "auth/cancelled-popup-request": {
    kind: "popup",
    title: "Facebookの画面が閉じられたため、登録を中断しました",
    detail: "もう一度「Facebookと連携する」を押して、Facebookの画面でログインと許可を最後まで進めてください。",
  },
  "auth/popup-blocked": {
    kind: "popup",
    title: "ブラウザがFacebookの画面をブロックしました",
    detail: "ブラウザの設定でこのサイトのポップアップを許可してから、もう一度お試しください。",
  },
  "auth/account-exists-with-different-credential": {
    kind: "popup",
    title: "このFacebookアカウントのメールアドレスは、既に別の方法でHINOMEに登録されています",
    detail: "登録済みのメールアドレスとパスワードでログインしてください。",
    suggestLogin: true,
  },
};

export const useAuthApi = () => {
  const router = useRouter();
  const [, setLoading] = useAtom(loadingAtom);
  const [, setInitializing] = useAtom(initializingAtom);
  const [instagram, setInstagram] = useAtom(instagramAccountInfoAtom);
  const instagramRef = useRef(instagram);
  instagramRef.current = instagram;
  const [idToken, setIdToken] = useAtom(idTokenAtom);
  const [authUser, setAuthUser] = useAtom(authUserAtom);
  const authApi = useMemo(() => new AppAuthApi(idToken, instagram?.instagram_business_id), [idToken, instagram]);

  const [referer] = useAtom(refererAtom);
  const [, setSignupLoading] = useAtom(signupLoadingAtom);
  const { addSignupReferer } = useSignupReferer();
  const { enqueueSnackbar } = useSnackbar();

  // id, 認証情報リセット => error, logout時に使用
  const resetAuth = useCallback(async () => {
    setAuthUser(null);
    setIdToken(null);
    setLoading(false);
    // Firebaseセッション残留を防止（別FBアカウントでsignInWithPopup後のログイン不能対策）
    try {
      await signOut(firebaseGetAuth);
    } catch (e) {
      console.error("signOut failed in resetAuth:", e);
    }
    // エンタープライズのデモはログイン不要で回遊させるため、ログイン画面へ送らない
    if (isCorporateDemoSession()) return;
    if (
      ![SIGNUP_PATH, PASSWORD_RESET_PATH, COLORME_LOGIN_PATH, COLORME_CALLBACK_PATH, COLORME_INSTALLED_PATH].includes(
        router.pathname,
      )
    ) {
      await router.push(LOGIN_PATH);
    }
  }, []);

  // member-me を読んで authUser と選択中の Instagram アカウントを揃える。失敗は呼び出し側で扱う
  const syncMember = useCallback(
    async (user: User, accessToken: string, first?: boolean) => {
      const memberApi = new AppAuthApi(accessToken);
      const member = await memberApi.memberMe();
      await setAuthUser(member);
      const currentAccountId = instagramRef.current?.instagram_business_id;
      const selectedAccount = currentAccountId
        ? member.instagram_accounts.find((acc) => acc.instagram_business_id === currentAccountId)
        : undefined;
      await setInstagram(selectedAccount || member.instagram_accounts[0]);
      // リロードのたびに出すと煩いので、ログイン直後だけ知らせる
      if (first) {
        enqueueSnackbar(`${user?.email + "で" || ""}ログイン中です`, {
          variant: "success",
        });
      }
      if (first && referer) {
        try {
          await addSignupReferer({
            id: member.id,
            firebaseAuthId: member.firebase_auth_user_id,
            refererCode: referer,
            firstName: member.first_name,
            lastName: member.last_name,
            email: member.email,
            companyName: member.company_name,
            gender: member.gender,
            instagramAccountCount: member.instagram_accounts.length,
            createTime: Date.now(),
            updateTime: Date.now(),
          });
        } catch (e) {
          console.error(e);
        }
      }
    },
    [referer],
  );

  // sync(サービスアクセス時に発火)
  const authSync = useCallback(
    async (refresh: boolean, first?: boolean) => {
      setLoading(true);
      try {
        // 戻り値は購読解除関数。呼び出し側で解除しないとリスナーが増え続ける
        return firebaseAuth.onAuthStateChanged(async (user) => {
          try {
            // 新規登録の途中は何もしない(登録処理側が最後に syncMember で揃える)
            if (signupInProgress) return;
            if (!user) return resetAuth();
            const accessToken = (await user?.getIdToken(true)) || null;
            await setIdToken(accessToken);
            if (!refresh || ["local"].includes(process.env.APP_ENV)) {
              try {
                await syncMember(user, accessToken, first);
                if (router.pathname === "/") {
                  await router.push(DASHBOARD_PATH);
                }
              } catch (e) {
                console.error(e?.response?.data?.detail || "", e);
                // 認証そのものを否定された時だけログアウトする。
                // 通信断・5xx・タイムアウトでセッションを捨てると、
                // バックエンドが一瞬詰まっただけでログイン画面に飛ばされる。
                const status = e?.response?.status;
                if (status === 401 || status === 403) return resetAuth();
                enqueueSnackbar("接続できませんでした。時間をおいて再度お試しください。", {
                  variant: "error",
                });
              }
            }
          } catch (e) {
            console.log(e);
          } finally {
            setLoading(false);
            // 以前は固定で 1 秒待ってからスプラッシュを閉じていた(全ページの初期表示が 1 秒遅れる)。
            // 管理画面・法人画面の分岐と同じく、認証と memberMe が終わり次第すぐ閉じる。
            setInitializing(false);
          }
        });
      } catch (e) {
        console.error(e);
      }
    },
    [idToken, authUser?.firebase_auth_user_id],
  );

  // FacebookAuth系
  const provider = new FacebookAuthProvider();
  provider.addScope("pages_show_list");
  provider.addScope("instagram_basic");
  provider.addScope("business_management");
  provider.addScope("pages_read_engagement");
  provider.addScope("instagram_manage_insights");
  provider.addScope("instagram_manage_comments");
  // 広告機能用スコープ（App Review通過済み）
  provider.addScope("ads_management"); // 広告の作成・編集・停止
  provider.addScope("ads_read"); // 広告一覧・設定状況の取得
  provider.addScope("instagram_content_publish");

  // SignUp
  // 失敗したときは理由を返す(画面に残して表示するため)。成功したときは何も返さない
  const authFacebookSignUpAndSync = useCallback(
    async (props: SignupRequests): Promise<SignupFailure | null | void> => {
      signupInProgress = true;
      try {
        setSignupLoading("Facebook認証中");
        let popupFailure: SignupFailure | null = null;
        const facebookSignUpResult = await new Promise<UserCredential>((resolve) => {
          signInWithPopup(firebaseGetAuth, provider)
            .then((result) => resolve(result))
            .catch((error) => {
              const code = String(error?.code || "");
              popupFailure = POPUP_ERROR_MESSAGES[code] || {
                kind: "popup",
                title: "Facebookに接続できませんでした",
                detail: `時間をおいてもう一度お試しください。（${code || error?.message || "unknown"}）`,
              };
              return resolve(null);
            });
        });
        if (!facebookSignUpResult) {
          setSignupLoading(null);
          return popupFailure;
        }
        setSignupLoading("アカウント情報を確認中");
        const user = facebookSignUpResult.user;
        // この操作で初めて作られた Firebase ユーザーか(既存ユーザーを消さないための判定)
        const isNewFirebaseUser = getAdditionalUserInfo(facebookSignUpResult)?.isNewUser ?? false;
        const accessToken = (await user.getIdToken()) || null;
        setIdToken(accessToken);
        const credential = FacebookAuthProvider.credentialFromResult(facebookSignUpResult);
        // メール/パスワードを Facebook のユーザーに紐づける
        const linkCredential = EmailAuthProvider.credential(props.email, props.password);
        const link = await linkWithCredential(user, linkCredential).then(
          (result) => ({ result, code: null as string | null }),
          (error) => ({
            result: null as UserCredential | null,
            code: String(error?.code || error?.message || "unknown"),
          }),
        );
        // 前回の登録が途中で失敗していると同じメールが既に紐づいている。その場合はやり直しとして続行する
        // (Firebase はメールを小文字で持つので、大文字小文字は区別しない)
        const retryWithSameEmail =
          link.code === "auth/provider-already-linked" &&
          (user.email || "").toLowerCase() === props.email.trim().toLowerCase();
        if (link.result || retryWithSameEmail) {
          if (retryWithSameEmail) {
            // 今回入力したパスワードでログインできるように揃える(直前に Facebook 認証済みなので更新できる)
            await updatePassword(user, props.password).catch((e) => console.error("updatePassword failed:", e));
          }
          // 途中経過は進捗画面に出ている。ここで「完了」と出すと、このあと失敗した時に食い違う
        } else {
          let failure: SignupFailure;
          // 別のメールが紐づいていても、HINOME の会員登録まで終わっていなければ前回の途中失敗の残り。
          // 残りを消して、次の一回で今回のメールで登録し直せるようにする(消さないと案内どおりログインしても 401 で戻される)
          const leftover = link.code === "auth/provider-already-linked" && (await isUnregisteredMember(accessToken));
          if (leftover) {
            failure = {
              kind: "link",
              title: "前回の登録が途中で止まっていました",
              detail: "やり直せるように前回の途中の情報を消しました。もう一度「Facebookと連携する」を押してください。",
            };
          } else if (link.code === "auth/provider-already-linked") {
            // この Facebook アカウントは別のメールアドレスで登録済み。そのメールの見当がつくように一部だけ出す
            const registered = maskEmail(user.email);
            failure = {
              kind: "link",
              title: "このFacebookアカウントは、別のメールアドレスで登録されています",
              detail: registered
                ? `登録済みのメールアドレス（${registered}）でログインしてください。`
                : "以前登録したメールアドレスでログインしてください。",
              suggestLogin: true,
            };
          } else if (link.code === "auth/email-already-in-use" || link.code === "auth/credential-already-in-use") {
            failure = {
              kind: "link",
              title: "このメールアドレスは、既に別のHINOMEアカウントで使われています",
              detail: "そのアカウントでログインするか、別のメールアドレスで登録してください。",
              suggestLogin: true,
            };
          } else {
            failure = {
              kind: "link",
              title: "メールアドレスを登録できませんでした",
              detail: `${LINK_ERROR_MESSAGES[link.code] || link.code}。入力内容を確かめて、もう一度お試しください。`,
            };
          }
          // 今回の操作で作られた Firebase ユーザーと、会員登録が終わっていない残りだけ消す。
          // 登録済みのユーザーを消すと本人がログインできなくなる
          if (isNewFirebaseUser || leftover) {
            await deleteUser(user).catch((e) => console.error("deleteUser failed:", e));
          }
          await signOut(firebaseGetAuth).catch((e) => console.error("signOut failed:", e));
          setSignupLoading(null);
          return failure;
        }
        setSignupLoading("Instagramを連携中");
        const facebookToken = credential.accessToken;
        try {
          const signUpApi = new AppAuthApi(accessToken);
          await signUpApi.signup({
            firebase_auth_user_id: user.uid,
            email: props.email,
            first_name: props.firstName,
            last_name: props.lastName,
            company_name: props.company,
            job_category: props.jobType,
            industry_category: props.industryCategory,
            birthday: props.birthday,
            gender: props.gender,
            phone_number: props.phoneNumber,
            facebook_id: user.uid,
            facebook_first_token: facebookToken,
            token_status: true,
            is_admin: false,
          });
        } catch (e) {
          // 既に登録済みの人が新規登録を押した場合は、そのままログインさせる(失敗として扱わない)
          if (e?.response?.data?.detail === "既に登録済みです") {
            try {
              await syncMember(user, accessToken);
              enqueueSnackbar("既にご登録済みのため、そのままログインしました", { variant: "success" });
              setSignupLoading(null);
              await router.push(DASHBOARD_PATH);
              return;
            } catch (syncError) {
              // 同じメールアドレスが別のアカウントで登録済みの場合はここに来る
              console.error(syncError);
              setSignupLoading(null);
              return {
                kind: "already_registered",
                title: "このメールアドレスは、既にHINOMEに登録されています",
                detail: "登録済みのアカウントでログインするか、別のメールアドレスで登録してください。",
                suggestLogin: true,
              } as SignupFailure;
            }
          }
          setSignupLoading(null);
          const detail: string = e?.response?.data?.detail || "";
          if (detail.includes("Instagramプロアカウントが見つかりませんでした")) {
            return {
              kind: "no_instagram",
              title: "Facebookページに紐づいたInstagramプロアカウントが見つかりませんでした",
              detail:
                "上の3つの設定がそろっているかご確認ください。そろっている場合は、Facebookの許可画面で、連携するFacebookページとInstagramアカウントにチェックを入れたかご確認ください。",
            } as SignupFailure;
          }
          return {
            kind: "other",
            title: "登録できませんでした",
            detail: `${
              detail || "時間をおいてもう一度お試しください。"
            } 解決しない場合は、右下のチャットからお問い合わせください。`,
          } as SignupFailure;
        }
        // 「アップデートのお知らせ」は既存ユーザー向けの変更点なので、登録したばかりの人には出さない
        // (出すと、登録直後に操作してほしい画面の上にかぶさる)
        await localforage.setItem(LocalForageKeys.LoginDialog.UpdateNoticeDialog, "read").catch(() => undefined);
        setSignupLoading("データを同期中");
        // 登録中はリスナーを止めているので、ここで直接 member-me を読んで画面の状態を揃える
        try {
          await syncMember(user, accessToken, true);
        } catch (e) {
          console.error(e);
          enqueueSnackbar("登録は完了しました。画面が表示されない場合は一度ログアウトして再度ログインしてください。", {
            variant: "warning",
          });
        }
        setSignupLoading(null);
        // カラーミーショップ経由の登録は、AuthProvider がショップを紐付けて連携画面へ送る。
        // ここでもダッシュボードへ送ると2つの遷移がぶつかり、どちらに着くかがタイミング次第になる
        // 紐付けが動かなかった時の保険として、数秒たっても登録画面のままならダッシュボードへ送る
        if (readColormePendingLink()) {
          setTimeout(() => {
            if (window.location.pathname === SIGNUP_PATH) router.push(DASHBOARD_PATH);
          }, 5000);
          return;
        }
        await router.push(DASHBOARD_PATH);
      } finally {
        signupInProgress = false;
      }
    },
    [authApi, syncMember],
  );

  // Email&Passwordでのログイン
  const authLoginWithEmail = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      try {
        signInWithEmailAndPassword(firebaseAuth, email, password)
          .then(async (cred) => {
            await localforage.setItem(LocalForageKeys.SigninForm.email, email);
            await localforage.setItem(LocalForageKeys.SigninForm.password, password);
            const linkUser = cred.user;
            const accessToken = (await linkUser.getIdToken()) || null;
            await setIdToken(accessToken);
            try {
              const checkApi = new AppAuthApi(accessToken);
              // await checkApi.checkFirebaseUser();
              const member = await checkApi.memberMe();
              await setAuthUser(member);
              await setInstagram(member.instagram_accounts[0]);
            } catch (e) {
              enqueueSnackbar(e?.response?.data?.detail || e?.message, {
                variant: "error",
              });
              return resetAuth();
            }
            enqueueSnackbar(`${email + "で" || ""}ログイン中です`, {
              variant: "success",
            });
            await router.push(DASHBOARD_PATH);
          })
          .catch((error) => {
            const code = error.code;
            if (code === "auth/user-not-found") {
              enqueueSnackbar("未登録のユーザーです。新規登録をお願いします。", {
                variant: "error",
              });
            } else if (code === "auth/wrong-password") {
              enqueueSnackbar("パスワードが一致しませんでした。", {
                variant: "error",
              });
            } else {
              enqueueSnackbar(error?.response?.data?.detail || error?.message, {
                variant: "error",
              });
            }
            return resetAuth();
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    },
    [authApi],
  );

  // カラーミーショップでのログイン（コールバックページが code/state を渡す）
  // shop.json の user_mail を既存ユーザーと照合 → Firebase カスタムトークンでサインイン
  const authLoginWithColorme = useCallback(
    async (code: string, state: string) => {
      setLoading(true);
      try {
        const res = await authApi.colormeLogin({ code, state });

        // 紐付け済みだが、カラーミーショップでHINOMEアプリが削除されている（＝解約）。
        // カラーミー経由の入口は閉じ、追加し直しを案内する画面へ戻す。
        // 引換券や保留中の連携は持たせない（持たせると通常ログイン後に自動紐付けが走る）。
        if (res.linked && !res.has_contract) {
          clearColormePendingLink();
          return await router.push(`${COLORME_INSTALLED_PATH}?shop=${encodeURIComponent(res.shop_id || "")}`);
        }

        // まだHINOMEアカウントと結び付いていないショップ。これは失敗ではなく
        // 「アプリを追加した直後」の正常な状態なので、引換券を持って案内画面へ戻す。
        if (!res.linked || !res.firebase_custom_token) {
          saveColormePendingLink({
            shopId: res.shop_id || "",
            shopLabel: res.shop_title || res.login_id || res.shop_id || "",
            linkTicket: res.link_ticket || undefined,
            hasContract: !!res.has_contract,
          });
          return await router.push(`${COLORME_INSTALLED_PATH}?shop=${encodeURIComponent(res.shop_id || "")}`);
        }

        const cred = await signInWithCustomToken(firebaseGetAuth, res.firebase_custom_token);
        const accessToken = (await cred.user.getIdToken()) || null;
        await setIdToken(accessToken);
        const member = await new AppAuthApi(accessToken).memberMe();
        await setAuthUser(member);
        await setInstagram(member.instagram_accounts[0]);
        enqueueSnackbar(`${res.email + "で" || ""}ログインしました`, {
          variant: "success",
        });
        await router.push(DASHBOARD_PATH);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || "カラーミーショップのアカウントでログインできませんでした", {
          variant: "error",
        });
        await resetAuth();
        await router.push(LOGIN_PATH);
      } finally {
        setLoading(false);
      }
    },
    [authApi],
  );

  // Facebookでのログイン
  const authLoginWithFacebook = useCallback(async () => {
    setLoading(true);
    try {
      signInWithPopup(firebaseGetAuth, provider)
        .then(async (cred) => {
          const linkUser = cred.user;
          const accessToken = (await linkUser.getIdToken()) || null;
          try {
            const checkApi = new AppAuthApi(accessToken);
            // await checkApi.checkFirebaseUser();
            await setIdToken(accessToken);
            const member = await checkApi.memberMe();
            await setAuthUser(member);
            await setInstagram(member.instagram_accounts[0]);
          } catch (e) {
            await enqueueSnackbar(e?.response?.data?.detail || e?.message, {
              variant: "error",
            });
            return resetAuth();
          }
          if (process.env.APP_ENV === "admin") {
            return await router.push(ADMIN_LOGIN_PATH);
          } else {
            enqueueSnackbar(`Facebookでログイン中です`, {
              variant: "success",
            });
            await router.push(DASHBOARD_PATH);
          }
        })
        .catch(async (error) => {
          const code = error.code;
          if (code === "auth/popup-closed-by-user") {
            enqueueSnackbar("Facebookに正常に接続できませんでした。", {
              variant: "error",
            });
          } else if (code === "auth/user-not-found") {
            enqueueSnackbar("未登録のユーザーです。新規登録をお願いします。", {
              variant: "error",
            });
          } else {
            enqueueSnackbar(error?.message || "error", {
              variant: "error",
            });
          }
          return resetAuth();
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (e) {
      console.error(e);
    }
  }, [authApi]);

  // ログアウト
  const authLogout = useCallback(async () => {
    setLoading(true);
    try {
      firebaseAuth
        .signOut()
        .then(async () => {
          enqueueSnackbar("ログアウトしました。", {
            variant: "info",
          });
          await router.push(LOGIN_PATH);
        })
        .catch((error) => {
          enqueueSnackbar(error?.message || "error", {
            variant: "error",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (e) {
      console.error(e);
    }
  }, [authApi]);

  // 会員情報のアップデート
  const upDateMemberMe = useCallback(
    async (props: APICompsSchema["UpdateMemberRequest"]) => {
      setLoading(true);
      try {
        await authApi.upDateMember(props);
        const member = await authApi.memberMe();
        await setAuthUser(member);
        enqueueSnackbar("会員情報の変更が完了しました。", {
          variant: "success",
        });
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [authApi],
  );

  // Facebook再連携（unlink→linkWithPopupでUIDを変えずにプロバイダーを差し替え）
  const upDateFacebookReLinked = useCallback(async () => {
    setLoading(true);
    try {
      const currentUser = firebaseGetAuth.currentUser;
      if (!currentUser) {
        enqueueSnackbar("ログイン状態が確認できません。再度ログインしてください。", { variant: "error" });
        return;
      }

      // 既存のFacebookプロバイダーがある場合はunlink
      const hasFacebook = currentUser.providerData?.some((p) => p.providerId === "facebook.com");
      let didUnlink = false;
      if (hasFacebook) {
        await unlink(currentUser, "facebook.com");
        didUnlink = true;
      }

      // 新しいFacebookアカウントをリンク（UIDは変わらない）
      const unlinkWarning = didUnlink
        ? "\n現在Facebookとの連携が解除された状態です。再度「Facebookと再連携する」ボタンから再連携してください。"
        : "";

      const tryLinkWithPopup = () =>
        new Promise<UserCredential>((resolve) => {
          linkWithPopup(currentUser, provider)
            .then((response) => resolve(response))
            .catch((error) => {
              resolve({ __error: error } as unknown as UserCredential);
            });
        });

      let result = await tryLinkWithPopup();

      // credential-already-in-use: 孤児化したFirebaseユーザーを削除してリトライ
      if ((result as any)?.__error?.code === "auth/credential-already-in-use") {
        const conflictError = (result as any).__error;
        const errorCredential = FacebookAuthProvider.credentialFromError(conflictError);
        if (errorCredential?.accessToken) {
          try {
            await authApi.resolveFacebookConflict({
              facebook_access_token: errorCredential.accessToken,
            });
            // 孤児ユーザー削除成功 → 1回目のcredentialでリンク（ポップアップ不要）
            const oauthCredential = FacebookAuthProvider.credential(errorCredential.accessToken);
            result = await linkWithCredential(currentUser, oauthCredential)
              .then((r) => r)
              .catch((error) => ({ __error: error } as unknown as UserCredential));
          } catch (e) {
            console.error("[resolveFacebookConflict] failed:", e);
          }
        }
      }

      // リトライ後もエラーの場合
      if ((result as any)?.__error) {
        const error = (result as any).__error;
        const code = error.code;
        if (code === "auth/popup-closed-by-user") {
          enqueueSnackbar(`Facebookに正常に接続できませんでした。${unlinkWarning}`, { variant: "error" });
        } else if (code === "auth/credential-already-in-use") {
          enqueueSnackbar(
            `このFacebookアカウントは別のユーザーに紐づいています。別のFacebookアカウントをお試しください。${unlinkWarning}`,
            { variant: "error" },
          );
        } else {
          enqueueSnackbar(`${error?.message || "Facebookの連携に失敗しました。"}${unlinkWarning}`, {
            variant: "error",
          });
        }
        return;
      }

      if (!result) return;

      const credential = FacebookAuthProvider.credentialFromResult(result);
      const facebookToken = credential.accessToken;
      const newFacebookId = result.user.providerData?.find((p) => p.providerId === "facebook.com")?.uid;

      // facebook_idとtokenをBackendに更新（UIDは同じなのでDB移行不要）
      if (newFacebookId) {
        await authApi.updateFacebookInfo({
          facebook_id: newFacebookId,
          facebook_first_token: facebookToken,
        });
      }

      // Instagramアカウント・会員情報を更新
      await authApi.upDateInstagramAccounts(facebookToken);
      await authApi.upDateMember({
        email: authUser.email,
        first_name: authUser.first_name,
        last_name: authUser.last_name,
        company_name: authUser.company_name,
        job_category: authUser.job_category,
        industry_category: authUser.industry_category,
        birthday: authUser.birthday,
        gender: authUser.gender,
        phone_number: authUser.phone_number,
        token_status: true,
      });

      const accessToken = (await result.user.getIdToken()) || null;
      const memberApi = new AppAuthApi(accessToken);
      const member = await memberApi.memberMe();
      await setAuthUser(member);
      await setInstagram(member.instagram_accounts[0]);
      await setIdToken(accessToken);
      enqueueSnackbar("Facebookの再連携が完了しました。", { variant: "success" });
    } catch (e) {
      console.error(e);
      enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [authApi, authUser]);

  const putUpdateFavoriteInstagram = useCallback(
    async (instagram_account_id: string) => {
      setLoading(true);
      try {
        return await authApi.upDateFavoriteInstagram(instagram_account_id);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [authApi],
  );

  const deleteInstagramAccountTunnel = useCallback(
    async (instagram_account_id: string) => {
      setLoading(true);
      try {
        await authApi.deleteInstagramAccount(instagram_account_id);
        enqueueSnackbar("連携解除に成功しました", {
          variant: "success",
        });
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [authApi],
  );

  //パスワードリセット w/email
  const resetPasswordWithEmail = useCallback(async (email: string) => {
    setLoading(true);
    await sendPasswordResetEmail(firebaseAuth, email)
      .then(() => {
        enqueueSnackbar("パスワード再設定メールを送信しました", {
          variant: "success",
        });
      })
      .catch((error) => {
        console.log(error);
        const code = error.code;
        if (code === "auth/user-not-found") {
          enqueueSnackbar("ユーザーが見つかりませんでした。", {
            variant: "error",
          });
        } else {
          enqueueSnackbar("メールの送信に失敗しました", {
            variant: "error",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  //パスワードリセット
  const resetPassword = useCallback(async (user: User, prevPassword: string, newPassword: string) => {
    setLoading(true);
    const credential = await EmailAuthProvider.credential(user.email, prevPassword);
    try {
      await reauthenticateWithCredential(user, credential).then(async (cred) => {
        await updatePassword(cred.user, newPassword)
          .then(() => {
            enqueueSnackbar("パスワードの更新に成功しました。", {
              variant: "success",
            });
          })
          .catch((error) => {
            console.log(error);
            const code = error.code;
            if (code) {
              enqueueSnackbar("パスワードのリセットに失敗しました。お問い合わせお願い致します。", {
                variant: "error",
              });
            }
          })
          .finally(() => setLoading(false));
      });
    } catch (error) {
      const code = error.code;
      if (code === "auth/wrong-password") {
        enqueueSnackbar("現在のパスワードが一致しませんでした。再度やり直してください。", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("パスワードのリセットに失敗しました。お問い合わせお願い致します。", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const resetEmail = useCallback(async (email: string, password: string, newEmail: string) => {
    const user = firebaseGetAuth.currentUser;
    setLoading(true);
    const credential = await EmailAuthProvider.credential(email, password);
    try {
      await reauthenticateWithCredential(user, credential).then(async (cred) => {
        await updateEmail(cred.user, newEmail)
          .then(async () => {
            enqueueSnackbar("メールアドレスの更新に成功しました。", {
              variant: "success",
            });
            const props: APICompsSchema["UpdateMemberRequest"] = {
              email: newEmail,
              first_name: authUser.first_name,
              last_name: authUser.last_name,
              company_name: authUser.company_name,
              job_category: authUser.job_category,
              industry_category: authUser.industry_category,
              birthday: authUser.birthday,
              gender: authUser.gender,
              phone_number: authUser.phone_number,
              token_status: authUser.token_status,
            };
            await authApi.upDateMember(props);
          })
          .catch(async (error) => {
            console.log(error);
            const code = error.code;
            if (code) {
              enqueueSnackbar("メールアドレスの更新に失敗しました。お問い合わせお願い致します。", {
                variant: "error",
              });
            }
            const props: APICompsSchema["UpdateMemberRequest"] = {
              email: email,
              first_name: authUser.first_name,
              last_name: authUser.last_name,
              company_name: authUser.company_name,
              job_category: authUser.job_category,
              industry_category: authUser.industry_category,
              birthday: authUser.birthday,
              gender: authUser.gender,
              phone_number: authUser.phone_number,
              token_status: authUser.token_status,
            };
            await authApi.upDateMember(props);
          })
          .finally(() => setLoading(false));
      });
    } catch (error) {
      const code = error.code;
      if (code === "auth/wrong-password") {
        enqueueSnackbar("現在のパスワードが一致しませんでした。再度やり直してください。", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("メールアドレスの更新に失敗しました。お問い合わせお願い致します。", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // 退会前チェック（有料契約中・唯一のオーナーなら止める理由が返る）
  const withdrawPreCheck = useCallback(async () => {
    try {
      return await authApi.withdrawPreCheck();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
      throw e;
    }
  }, [authApi]);

  // 退会。成功したらこの端末のログイン状態を消してログイン画面へ
  const withdrawAccount = useCallback(
    async (props: APICompsSchema["MemberWithdrawRequest"]) => {
      setLoading(true);
      try {
        const result = await authApi.withdraw(props);
        enqueueSnackbar(result.message, { variant: "success" });
        await resetAuth();
        return result;
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.detail || e?.message, { variant: "error" });
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [authApi, resetAuth],
  );

  return {
    authSync,
    authFacebookSignUpAndSync,
    withdrawPreCheck,
    withdrawAccount,
    authLoginWithEmail,
    authLoginWithFacebook,
    authLoginWithColorme,
    authLogout,
    upDateMemberMe,
    upDateFacebookReLinked,
    putUpdateFavoriteInstagram,
    deleteInstagramAccountTunnel,
    resetPasswordWithEmail,
    resetPassword,
    resetEmail,
  };
};
